import { forwardRef, useImperativeHandle, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSnackbar } from 'notistack';
import { Eye } from 'lucide-react';
import Button from 'src/componentes/button';
import ContentManager from 'src/componentes/content-manager';
import FormularioGlobalBuilder from './FormularioGlobalBuilder';
import FormularioGlobalCopiarColar from './FormularioGlobalCopiarColar';
import FormularioGlobalPreviewDialog from './FormularioGlobalPreviewDialog';
import { copyFormularioStructure } from './formularioGlobalBranching';
import {
  getFormularioValidationErrors,
  getFormularioValidationToastError,
  hasValidationErrors,
  normalizeFormulario,
} from './formularioGlobalModel';

const FormularioGlobalEditor = forwardRef(
  (
    { value, onChange, disabled, tipoFormulario, titleError, headerContent },
    ref
  ) => {
    const intl = useIntl();
    const { enqueueSnackbar } = useSnackbar();
    const [validationErrors, setValidationErrors] = useState({});
    const [validated, setValidated] = useState(false);
    const formulario = useMemo(
      () => normalizeFormulario(value, intl, tipoFormulario),
      [
        value,
        value?.descricao,
        value?.ativo,
        value?.formatoExibicao,
        intl,
        tipoFormulario,
      ]
    );
    const isDisabled = disabled || formulario.bloqueado;

    useImperativeHandle(ref, () => ({
      validateFormularioGlobal: () => {
        const errors = getFormularioValidationErrors(
          formulario,
          formulario.randomizarPerguntas
        );
        setValidated(true);
        setValidationErrors(errors);
        const toastError = getFormularioValidationToastError(errors);
        if (toastError) {
          enqueueSnackbar(
            intl.formatMessage({ id: toastError.id }, toastError.values),
            { variant: 'error' }
          );
        }

        return !hasValidationErrors(errors);
      },
    }));

    function handleChange(nextValue) {
      if (isDisabled) return;

      const nextFormulario = normalizeFormulario(
        nextValue,
        intl,
        tipoFormulario
      );
      if (validated) {
        setValidationErrors(
          getFormularioValidationErrors(
            nextFormulario,
            nextFormulario.randomizarPerguntas
          )
        );
      }
      onChange(nextFormulario);
    }

    function handleColar(formularioColado) {
      handleChange({
        ...formulario,
        titulo: formularioColado.titulo || '',
        descricao: formularioColado.descricao || '',
        ...copyFormularioStructure(formularioColado),
      });
    }

    return (
      <FormularioGlobalBuilder
        value={formulario}
        tipoFormulario={tipoFormulario}
        disabled={isDisabled}
        exibirTipoFormularioIdentificacao={false}
        blockedMessage={
          formulario.bloqueado
            ? intl.formatMessage({ id: 'labelFormularioGlobalBloqueado' })
            : null
        }
        titleValidationMessage={titleError}
        titleRequired
        validationErrors={validationErrors}
        randomizarPerguntas={formulario.randomizarPerguntas}
        onChange={handleChange}
        headerContent={headerContent}
        headerActions={
          <>
            <Button
              type="button"
              color="link"
              leftIcon={Eye}
              iconSize={18}
              className="tw:p-0 tw:text-muted-foreground tw:no-underline"
              onClick={() =>
                ContentManager.addContent(
                  <FormularioGlobalPreviewDialog formulario={formulario} />
                )
              }
            >
              {intl.formatMessage({ id: 'visualizar' })}
            </Button>
            <FormularioGlobalCopiarColar
              disabled={isDisabled}
              formulario={formulario}
              onColar={handleColar}
            />
          </>
        }
      />
    );
  }
);

export default FormularioGlobalEditor;
